// Markup UI: topbar tool buttons, drawing hint bar, save-comment form, sidebar list.
import { api, type MarkupOut } from "../api";
import { MARKUP_COLORS, MARKUP_WIDTHS, type MarkupManager, type MarkupTool, type DraftResult } from "../cesium/markup";
import { $activeProjectId, $selectedItemId, $selectedItemName, $user } from "../store";
import { toast } from "./toast";
import { isTouch } from "./mobile";

const TOOLS: { tool: MarkupTool; label: string; title: string }[] = [
  { tool: "pin", label: "📍 Comment", title: "Drop a comment pin (1 click)" },
  { tool: "freehand", label: "✏ Draw", title: "Pencil: press and drag to draw freehand" },
  { tool: "polygon", label: "▰ Area", title: "Outline an area (right-click to finish)" },
  { tool: "measure", label: "📏 Measure", title: "Measure distance (2 clicks)" },
];

const HINTS: Record<string, string> = {
  pin: "Click anywhere to drop the pin · Esc to cancel",
  freehand: "Press and drag to draw a stroke · add as many as you like · pick colour + thickness · click Done to save them as ONE issue · Esc to discard",
  polygon: "Click to add corners · right-click to close the area · Esc to cancel",
  measure: "Click two points to measure · Esc to cancel",
};

// Touch wording: no cursor, no right-click (Area closes with the Finish button), no Esc key.
const TOUCH_HINTS: Record<string, string> = {
  pin: "Tap the map to drop the pin",
  freehand: "Drag a finger to draw · the map stays still while drawing · tap Done to save the strokes as ONE issue",
  polygon: "Tap to add corners · tap Finish to close the area",
  measure: "Tap two points to measure",
};

const HIDE_DONE_KEY = "geaar.markup.hideDone";

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

export function mountMarkupTools(
  topbar: HTMLElement,
  listEl: HTMLElement,
  markup: MarkupManager,
  viewerCanvas: () => HTMLCanvasElement,
  onChanged: () => void,
  getItemName: (itemId: number) => string | null = () => null,
  onSelectItem: (itemId: number) => void = () => {},
): { refresh: () => Promise<void> } {
  let listMode: "all" | "layer" = "all";
  let showDone = false; // collapsed "completed" log state, kept across refreshes
  // Done comments clutter the globe once a round of feedback has been worked through, so they
  // can be taken off the map entirely. Sticky, because it is a way of working, not a one-off view.
  let hideDone = localStorage.getItem(HIDE_DONE_KEY) === "1";
  let lastMarkups: MarkupOut[] = [];
  let lastSig = " "; // signature of the last drawn markup set (skip redraw when unchanged)
  const group = document.createElement("div");
  group.className = "markup-tools";
  const buttons = new Map<MarkupTool, HTMLButtonElement>();

  for (const t of TOOLS) {
    const b = document.createElement("button");
    b.className = "tool-btn";
    b.textContent = t.label;
    b.title = t.title;
    b.addEventListener("click", () => {
      markup.setTool(markup.getTool() === t.tool ? "none" : t.tool);
    });
    buttons.set(t.tool, b);
    group.append(b);
  }

  // Draw options live in a dropdown under the Draw tool: colour + thickness together,
  // shown only while Draw is active (no permanent clutter in the toolbar).
  const drawMenu = document.createElement("div");
  drawMenu.className = "draw-menu";

  const colLabel = document.createElement("span");
  colLabel.className = "draw-menu-label";
  colLabel.textContent = "Colour";
  const swatches = document.createElement("span");
  swatches.className = "markup-colors";
  for (const c of MARKUP_COLORS) {
    const sw = document.createElement("button");
    sw.className = "swatch";
    sw.style.background = c.css;
    sw.title = c.name;
    if (c.css === markup.getColor()) sw.classList.add("active");
    sw.addEventListener("click", () => {
      markup.setColor(c.css);
      swatches.querySelectorAll(".swatch").forEach((s) => s.classList.remove("active"));
      sw.classList.add("active");
    });
    swatches.append(sw);
  }

  const wLabel = document.createElement("span");
  wLabel.className = "draw-menu-label";
  wLabel.textContent = "Thickness";
  const widths = document.createElement("span");
  widths.className = "markup-widths";
  for (const w of MARKUP_WIDTHS) {
    const wb = document.createElement("button");
    wb.className = "width-btn";
    wb.textContent = "▬";
    wb.style.fontSize = `${Math.max(8, w.px - 2)}px`;
    wb.title = `${w.name} stroke`;
    if (w.px === markup.getPenWidth()) wb.classList.add("active");
    wb.addEventListener("click", () => {
      markup.setPenWidth(w.px);
      widths.querySelectorAll(".width-btn").forEach((s) => s.classList.remove("active"));
      wb.classList.add("active");
    });
    widths.append(wb);
  }
  drawMenu.append(colLabel, swatches, wLabel, widths);
  document.body.append(drawMenu);

  function showDrawMenu(show: boolean): void {
    if (!show) {
      drawMenu.classList.remove("show");
      return;
    }
    const btn = buttons.get("freehand");
    if (btn) {
      const r = btn.getBoundingClientRect();
      drawMenu.style.top = `${r.bottom + 6}px`;
      drawMenu.style.left = `${r.left}px`;
    }
    drawMenu.classList.add("show");
  }

  const capture = document.createElement("button");
  capture.className = "tool-btn";
  capture.textContent = "📷 Capture";
  capture.title = "Save a screenshot of the current view (with markup)";
  capture.addEventListener("click", async () => {
    const pid = $activeProjectId.get();
    if (pid == null) return;
    const canvas = viewerCanvas();
    const data = canvas.toDataURL("image/png");
    await api.saveScreenshot(pid, data, undefined, JSON.stringify(markup.cameraState()));
    toast("Screenshot saved", "ok");
  });
  group.append(capture);
  topbar.append(group);

  // Drawing hint bar: appears while a tool is armed so the gestures are never a mystery.
  const hint = document.createElement("div");
  hint.className = "draw-hint";
  document.body.append(hint);

  // Top-right active-tool panel: names the current tool and explains what it does.
  const toolCard = document.createElement("div");
  toolCard.className = "tool-card";
  document.body.append(toolCard);
  const TOOL_TITLE: Record<string, string> = {
    pin: "Comment", freehand: "Draw", polygon: "Area", measure: "Measure",
  };
  const TOOL_DESC: Record<string, string> = {
    pin: "Click anywhere on the map to drop a comment pin on the selected layer.",
    freehand: "Pick a colour and thickness, draw as many strokes as you like, then click Done to save them as one issue.",
    polygon: "Click or tap to add corners, then right-click or press Finish to close the outline.",
    measure: "Click two points to read the distance between them.",
  };
  function showToolCard(tool: MarkupTool): void {
    if (tool === "none") {
      toolCard.classList.remove("show");
      return;
    }
    toolCard.innerHTML =
      `<div class="tc-title">${TOOL_TITLE[tool] ?? tool}</div>` +
      `<div class="tc-desc">${TOOL_DESC[tool] ?? ""}</div>` +
      (isTouch ? "" : `<div class="tc-hint">Esc to cancel</div>`);
    toolCard.classList.add("show");
  }

  markup.onToolChange = (tool) => {
    for (const [t, b] of buttons) b.classList.toggle("active", t === tool);
    showDrawMenu(tool === "freehand");
    showToolCard(tool);
    if (tool === "none") {
      hint.classList.remove("show");
      hint.innerHTML = "";
      return;
    }
    const target = $selectedItemName.get();
    const tip = (isTouch ? TOUCH_HINTS : HINTS)[tool] + (target ? `  ·  attaching to: ${target}` : "");
    if (tool === "freehand") {
      // Freehand gets explicit Done / Cancel so every stroke folds into one issue.
      hint.innerHTML =
        `<span class="dh-tip">${tip}</span>` +
        `<span class="dh-actions">` +
        `<button class="tool-btn fh-done" disabled>✓ Done</button>` +
        `<button class="tool-btn fh-cancel">✕ Cancel</button></span>`;
      hint.querySelector(".fh-done")!.addEventListener("click", () => markup.finishFreehand());
      hint.querySelector(".fh-cancel")!.addEventListener("click", () => markup.setTool("none"));
    } else if (tool === "polygon" || tool === "polyline" || isTouch) {
      // Area/line close with right-click on desktop; touch has none, so every tool gets explicit
      // buttons there (and Area / line get them everywhere - a button beats a hidden gesture).
      const closes = tool === "polygon" || tool === "polyline";
      hint.innerHTML =
        `<span class="dh-tip">${tip}</span>` +
        `<span class="dh-actions">` +
        (closes ? `<button class="tool-btn sh-finish">✓ Finish</button>` : "") +
        `<button class="tool-btn sh-cancel">✕ Cancel</button></span>`;
      hint.querySelector(".sh-finish")?.addEventListener("click", () => markup.finishShape());
      hint.querySelector(".sh-cancel")!.addEventListener("click", () => markup.setTool("none"));
    } else {
      hint.textContent = tip;
    }
    hint.classList.add("show");
  };

  // Reflect the accumulating stroke count on the Done button (and enable it).
  markup.onFreehandChange = (count) => {
    const done = hint.querySelector<HTMLButtonElement>(".fh-done");
    if (!done) return;
    done.textContent = count ? `✓ Done (${count})` : "✓ Done";
    done.disabled = count === 0;
  };

  // --- save form shown when a drawing completes (incl. a finished freehand session) ---
  markup.onDraftComplete = (draft) => showSaveForm(draft);

  // --- click a markup on the globe -> readable comment card ---
  markup.onMarkupSelect = (m) => {
    document.querySelector(".markup-card")?.remove();
    const card = document.createElement("div");
    card.className = "markup-form markup-card";
    const icon = m.type === "pin" ? "📍" : m.type === "measure" ? "📏" : m.type === "polygon" ? "▰" : "✏";
    const layerName = m.register_item_id != null ? getItemName(m.register_item_id) : null;
    const done = m.resolved
      ? `<span class="done-badge">✓ Done · ${fmtTime(m.resolved_at)}</span>`
      : "";
    card.innerHTML = `
      <div class="markup-form-title">${icon} ${m.comment || m.label || m.type}
        ${layerName ? `<span class="attach ok">${layerName}</span>` : ""}</div>
      <div class="markup-card-meta">${m.author ? `${m.author} · ` : ""}${fmtTime(m.created_at)}${m.label && m.comment ? ` · ${m.label}` : ""} ${done}</div>
      <div class="markup-form-actions">
        <button class="tool-btn resolve">${m.resolved ? "↺ Reopen" : "✓ Mark done"}</button>
        <button class="tool-btn view">View from here</button>
        <button class="tool-btn close">Close</button>
      </div>`;
    document.body.append(card);
    card.querySelector(".resolve")!.addEventListener("click", async () => {
      const pid = $activeProjectId.get();
      if (pid == null) return;
      await api.patchMarkup(pid, m.id, { resolved: !m.resolved });
      card.remove();
      await refresh();
      onChanged();
    });
    card.querySelector(".view")!.addEventListener("click", () => {
      if (m.camera) markup.restoreCamera(m.camera);
    });
    card.querySelector(".close")!.addEventListener("click", () => card.remove());
    window.setTimeout(() => {
      document.addEventListener("click", function dismiss(ev) {
        if (!card.contains(ev.target as Node)) {
          card.remove();
          document.removeEventListener("click", dismiss);
        }
      });
    }, 100);
  };

  function showSaveForm(draft: DraftResult): void {
    document.querySelector(".markup-form")?.remove();
    const form = document.createElement("div");
    form.className = "markup-form";
    const isMeasure = draft.type === "measure";
    // No layer title here - the selected layer is already clear from the tree on the left.
    form.innerHTML = `
      <div class="markup-form-title">${isMeasure ? `Measured: ${draft.label}` : "Add a comment"}</div>
      <input class="markup-comment" type="text" placeholder="Comment… (Enter to save, Esc to discard)" />
      <div class="markup-form-actions">
        <button class="tool-btn save">Save</button>
        <button class="tool-btn discard">Discard</button>
      </div>`;
    document.body.append(form);
    const commentEl = form.querySelector<HTMLInputElement>(".markup-comment")!;
    commentEl.focus();

    const save = async () => {
      const pid = $activeProjectId.get();
      if (pid == null) return form.remove();
      await api.createMarkup(pid, {
        type: draft.type,
        geometry_json: JSON.stringify(draft.geometry),
        style_json: JSON.stringify({ color: draft.color, width: draft.width }),
        label: draft.label ?? null,
        comment: commentEl.value || null,
        camera_json: JSON.stringify(markup.cameraState()),
        register_item_id: $selectedItemId.get(),
        author: $user.get() || null,
      });
      form.remove();
      const target = $selectedItemName.get();
      toast(target ? `Markup saved on ${target}` : "Markup saved", "ok");
      await refresh();
      onChanged();
    };
    form.querySelector(".save")!.addEventListener("click", () => void save());
    commentEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void save();
      if (e.key === "Escape") form.remove();
    });
    form.querySelector(".discard")!.addEventListener("click", () => form.remove());
  }

  // --- sidebar list ---
  /** Push the current markups (minus anything filtered out) to the globe and the list. */
  function redraw(): void {
    markup.showMarkups(hideDone ? lastMarkups.filter((m) => !m.resolved) : lastMarkups);
    renderList();
  }

  async function refresh(): Promise<void> {
    const pid = $activeProjectId.get();
    if (pid == null) return;
    const { markups } = await api.listMarkups(pid);
    const sig = JSON.stringify(markups);
    if (sig === lastSig) return; // unchanged since last draw - don't flicker / close cards
    lastSig = sig;
    lastMarkups = markups;
    redraw();
  }

  // Re-filter the list when the selected layer changes in "This layer" mode.
  $selectedItemId.subscribe(() => {
    if (listMode === "layer") renderList();
  });

  function renderList(): void {
    listEl.innerHTML = "";
    // The log holds BOTH kinds: map markup (pins/draws with geometry) AND the dimension-tagged
    // layer comments (no geometry - they also live in the review bar, but this is the one place
    // to see every comment at once). Clicking a layer comment opens its layer; clicking a map
    // markup flies to the view it was drawn from.
    // The badge counts what is actually in play: with Done hidden it is the open issues.
    const counted = hideDone ? lastMarkups.filter((m) => !m.resolved) : lastMarkups;
    const countEl = document.getElementById("markup-count");
    if (countEl) countEl.textContent = counted.length ? String(counted.length) : "";

    // Scope filter: every comment, or only the selected layer's.
    const bar = document.createElement("div");
    bar.className = "markup-filter";
    for (const [mode, label, title] of [
      ["all", "All", "Every comment + markup in the project"],
      ["layer", "This layer", "Only comments/markup on the selected layer"],
    ] as const) {
      const b = document.createElement("button");
      b.className = "filter-chip" + (listMode === mode ? " active" : "");
      b.textContent = label;
      b.title = title;
      b.addEventListener("click", () => {
        listMode = mode;
        renderList();
      });
      bar.append(b);
    }
    // Separate toggle, not a third scope mode: it stacks on top of All / This layer and
    // takes done comments off the GLOBE too, which the collapsible log below never did.
    const spacer = document.createElement("span");
    spacer.className = "filter-spacer";
    const hideBtn = document.createElement("button");
    hideBtn.className = "filter-chip" + (hideDone ? " active" : "");
    hideBtn.textContent = hideDone ? "✓ Done hidden" : "Hide done";
    hideBtn.title = hideDone
      ? "Show completed comments again, on the map and in this list"
      : "Hide completed comments from the map and this list";
    hideBtn.addEventListener("click", () => {
      hideDone = !hideDone;
      localStorage.setItem(HIDE_DONE_KEY, hideDone ? "1" : "0");
      redraw();
    });
    bar.append(spacer, hideBtn);
    listEl.append(bar);

    let rows = [...lastMarkups].reverse();
    if (hideDone) rows = rows.filter((m) => !m.resolved);
    if (listMode === "layer") {
      const sel = $selectedItemId.get();
      rows = rows.filter((m) => m.register_item_id === sel);
    }
    if (!rows.length) {
      // With Done hidden, "nothing here" usually means "nothing OPEN here" - say which,
      // so an empty panel is never mistaken for lost comments.
      const hiddenDone = hideDone
        ? lastMarkups.filter(
            (m) =>
              m.resolved &&
              (listMode !== "layer" || m.register_item_id === $selectedItemId.get()),
          ).length
        : 0;
      const hint = document.createElement("div");
      hint.className = "hint";
      hint.textContent = hiddenDone
        ? `All comments completed ✓ · ${hiddenDone} done hidden`
        : listMode === "layer"
          ? "No comments on the selected layer."
          : "No comments yet. Flag a layer or use the markup tools above the globe.";
      listEl.append(hint);
      return;
    }

    // Inline-edit a comment's text in place (Enter/blur saves, Esc reverts).
    const beginEdit = (m: MarkupOut, textEl: HTMLElement): void => {
      const input = document.createElement("input");
      input.className = "markup-editbox";
      input.value = m.comment || "";
      textEl.replaceWith(input);
      input.focus();
      input.select();
      let finished = false;
      const commit = async (save: boolean): Promise<void> => {
        if (finished) return;
        finished = true;
        const pid = $activeProjectId.get();
        const text = input.value.trim();
        if (save && pid != null && text && text !== (m.comment || "")) {
          await api.patchMarkup(pid, m.id, { comment: text, actor: $user.get() || null });
          toast("Comment updated", "ok");
          await refresh();
          onChanged();
        } else {
          renderList(); // revert
        }
      };
      input.addEventListener("click", (e) => e.stopPropagation());
      input.addEventListener("keydown", (e) => {
        e.stopPropagation();
        if (e.key === "Enter") void commit(true);
        else if (e.key === "Escape") void commit(false);
      });
      input.addEventListener("blur", () => void commit(true));
    };

    const renderRow = (m: MarkupOut, into: HTMLElement): void => {
      const isLayerComment = !!m.dimension; // dimension-tagged note attached to a layer
      const row = document.createElement("div");
      row.className =
        "markup-row" + (m.resolved ? " resolved" : "") + (isLayerComment ? " layer-comment" : "");
      row.title = isLayerComment
        ? "Click to open this layer"
        : "Click to fly to the view this was drawn from";
      const icon = isLayerComment
        ? "💬"
        : m.type === "pin" ? "📍" : m.type === "measure" ? "📏" : m.type === "polygon" ? "▰" : "✏";
      const layerName = m.register_item_id != null ? getItemName(m.register_item_id) : null;
      const dimBadge = m.dimension
        ? `<span class="markup-dim dim-${m.dimension.toLowerCase()}">${m.dimension}</span>`
        : "";
      const meta = m.resolved
        ? `<span class="markup-time done">✓ done ${fmtTime(m.resolved_at)}</span>`
        : `<span class="markup-time">${fmtTime(m.created_at)}</span>`;
      const iconEl = document.createElement("span");
      iconEl.className = "markup-icon";
      iconEl.textContent = icon;
      const body = document.createElement("span");
      body.className = "markup-body";
      const textEl = document.createElement("span");
      textEl.className = "markup-text";
      textEl.textContent = m.comment || m.label || m.type;
      const sub = document.createElement("span");
      sub.className = "markup-sub";
      sub.innerHTML = `${dimBadge}${layerName ? `<span class="markup-layer">${layerName}</span>` : ""}${meta}`;
      body.append(textEl, sub);
      row.append(iconEl, body);
      row.addEventListener("click", () => {
        if (isLayerComment) {
          if (m.register_item_id != null) onSelectItem(m.register_item_id);
        } else if (m.camera) {
          markup.restoreCamera(m.camera);
        }
      });
      const editBtn = document.createElement("button");
      editBtn.className = "link-btn";
      editBtn.textContent = "✎";
      editBtn.title = "Edit this comment";
      editBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        beginEdit(m, textEl);
      });
      row.append(editBtn);
      const doneBtn = document.createElement("button");
      doneBtn.className = "link-btn" + (m.resolved ? " active" : "");
      doneBtn.textContent = "✓";
      doneBtn.title = m.resolved ? "Reopen (mark not done)" : "Mark this issue done";
      doneBtn.addEventListener("click", async (e) => {
        e.stopPropagation();
        const pid = $activeProjectId.get();
        if (pid == null) return;
        await api.patchMarkup(pid, m.id, { resolved: !m.resolved, actor: $user.get() || null });
        toast(m.resolved ? "Reopened" : "Marked done", "ok");
        await refresh();
        onChanged();
      });
      row.append(doneBtn);
      const del = document.createElement("button");
      del.className = "link-btn danger";
      del.textContent = "✕";
      del.title = "Delete markup";
      del.addEventListener("click", async (e) => {
        e.stopPropagation();
        const pid = $activeProjectId.get();
        if (pid == null) return;
        await api.deleteMarkup(pid, m.id);
        toast("Markup deleted", "info");
        await refresh();
        onChanged();
      });
      row.append(del);
      into.append(row);
    };

    // Active issues stay in the main list; completed ones tuck into a collapsible log
    // so the markup area stays clean as things get ticked off.
    const active = rows.filter((m) => !m.resolved);
    const done = rows.filter((m) => m.resolved);
    if (!active.length && done.length) {
      const allDone = document.createElement("div");
      allDone.className = "hint";
      allDone.textContent = "All comments completed ✓";
      listEl.append(allDone);
    }
    for (const m of active) renderRow(m, listEl);
    if (done.length) {
      const toggle = document.createElement("button");
      toggle.className = "done-log-toggle" + (showDone ? " open" : "");
      toggle.textContent = `✓ ${done.length} completed`;
      const log = document.createElement("div");
      log.className = "done-log";
      log.style.display = showDone ? "block" : "none";
      for (const m of done) renderRow(m, log);
      toggle.addEventListener("click", () => {
        showDone = !showDone;
        log.style.display = showDone ? "block" : "none";
        toggle.classList.toggle("open", showDone);
      });
      listEl.append(toggle, log);
    }
    if (hideDone) {
      // Keep the hidden count visible: an unexplained drop in comments reads like data loss.
      const n = lastMarkups.filter(
        (m) =>
          m.resolved &&
          (listMode !== "layer" || m.register_item_id === $selectedItemId.get()),
      ).length;
      if (n) {
        const note = document.createElement("button");
        note.className = "done-log-toggle";
        note.textContent = `✓ ${n} completed hidden - show`;
        note.addEventListener("click", () => {
          hideDone = false;
          localStorage.setItem(HIDE_DONE_KEY, "0");
          redraw();
        });
        listEl.append(note);
      }
    }
  }

  return { refresh };
}
